import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronDown, Calculator } from "lucide-react";
import { PLANS, PRICING_DISCLAIMER, ADDONS, CALC_OPTIONS, waLink } from "@/data/site";
import { SectionHeading } from "@/components/pp/Shared";
import { track } from "@/lib/api";
import { scrollTo } from "@/components/pp/Navbar";

const inr = (n) => "\u20b9" + n.toLocaleString("en-IN");

function PlanCard({ plan, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08 }}
      className={`relative rounded-2xl p-7 flex flex-col ${plan.popular ? "bg-[#0d0d12] pp-recommended" : "pp-card"}`}
      data-testid={`plan-${plan.id}`}
    >
      {plan.popular && (
        <span className="absolute -top-3 left-7 bg-[#f59e0b] text-black text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
          Most Popular
        </span>
      )}
      <h3 className="font-display uppercase text-2xl tracking-wide text-white">{plan.name}</h3>
      <div className="mt-4 flex items-end gap-1">
        <span className="font-display text-4xl text-[#f59e0b]">{plan.price}</span>
        <span className="text-slate-500 text-sm mb-1">{plan.period}</span>
      </div>
      <p className="mt-3 text-sm text-slate-400 min-h-[40px]">{plan.best}</p>
      <button
        onClick={() => { track("pricing_click", { plan: plan.id }); scrollTo("planner"); }}
        data-testid={`plan-cta-${plan.id}`}
        className={`mt-5 w-full py-3.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
          plan.popular ? "bg-[#f59e0b] text-black hover:bg-white" : "border border-white/15 text-white hover:bg-white/5"}`}>
        {plan.cta}
      </button>
      {plan.note && <p className="mt-4 text-xs text-[#f59e0b]">{plan.note}</p>}
      <ul className="mt-6 space-y-2.5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
            <Check size={16} className="text-[#f59e0b] shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function Calc() {
  const [sel, setSel] = useState({
    platforms: CALC_OPTIONS.platforms[0], posts: CALC_OPTIONS.posts[0], reels: CALC_OPTIONS.reels[1],
    ads: CALC_OPTIONS.ads[0], seo: CALC_OPTIONS.seo[0], shoot: CALC_OPTIONS.shoot[0],
    influencer: CALC_OPTIONS.influencer[0], website: CALC_OPTIONS.website[0],
  });
  const total = useMemo(() => Object.values(sel).reduce((a, o) => a + o.value, 0), [sel]);
  const labels = { platforms: "Platforms", posts: "Posts / month", reels: "Reels / month", ads: "Ad management", seo: "SEO", shoot: "Video shoot", influencer: "Influencer", website: "Website" };

  return (
    <div className="pp-glass rounded-2xl p-7 md:p-10" data-testid="plan-calculator">
      <div className="flex items-center gap-3 mb-8">
        <Calculator className="text-[#f59e0b]" size={22} />
        <h3 className="font-display uppercase text-2xl md:text-3xl text-white">Build Your Plan</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
        {Object.keys(CALC_OPTIONS).map((key) => (
          <div key={key}>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">{labels[key]}</p>
            <div className="flex flex-wrap gap-2">
              {CALC_OPTIONS[key].map((opt) => (
                <button key={opt.label}
                  onClick={() => setSel((s) => ({ ...s, [key]: opt }))}
                  data-testid={`calc-${key}-${opt.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    sel[key].label === opt.label ? "bg-[#f59e0b] text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-9 pt-7 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400">Indicative estimate</p>
          <p className="font-display text-4xl md:text-5xl pp-text-gradient mt-1" data-testid="calc-total">{inr(total)}<span className="text-lg text-slate-500">/mo</span></p>
          <p className="text-xs text-slate-500 mt-1">Final pricing is confirmed after a discovery call.</p>
        </div>
        <a href={waLink(`Hi PixelPapad, my Build-Your-Plan estimate is ~${inr(total)}/month. Can we finalise it?`)}
          target="_blank" rel="noopener noreferrer"
          onClick={() => track("calculator_submit", { total })}
          data-testid="calc-cta"
          className="inline-flex items-center justify-center bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-xs px-7 py-4 hover:bg-white transition-colors">
          Confirm on WhatsApp
        </a>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [openAddons, setOpenAddons] = useState(false);
  return (
    <section id="pricing" className="relative py-24 md:py-32 bg-[#050507]" data-testid="pricing">
      <div className="pp-container">
        <SectionHeading overline="Pricing" title="Flexible Plans for Every Stage of Growth"
          sub="Choose a starting plan or request a customised marketing system." testid="pricing-heading" />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-start">
          {PLANS.map((p, i) => <PlanCard key={p.id} plan={p} i={i} />)}
        </div>

        <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="pricing-disclaimer">{PRICING_DISCLAIMER}</p>

        {/* Add-ons */}
        <div className="mt-16">
          <button onClick={() => setOpenAddons((v) => !v)} data-testid="addons-toggle"
            className="w-full flex items-center justify-between pp-card rounded-xl px-6 py-5">
            <span className="font-display uppercase text-xl md:text-2xl text-white">À la carte Add-ons</span>
            <ChevronDown className={`text-[#f59e0b] transition-transform ${openAddons ? "rotate-180" : ""}`} />
          </button>
          <motion.div initial={false} animate={{ height: openAddons ? "auto" : 0, opacity: openAddons ? 1 : 0 }}
            className="overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-4">
              {ADDONS.map((a) => (
                <div key={a.name} className="pp-card rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid={`addon-${a.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <span className="text-sm text-slate-300">{a.name}</span>
                  <span className="text-sm font-semibold text-[#f59e0b] whitespace-nowrap">{a.price}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-16">
          <Calc />
        </div>
      </div>
    </section>
  );
}
